import { useCallback, useEffect, useRef } from "react";
import type { AppState } from "../../state.js";
import { downloadAsZip } from "../../io/zip.js";
import { taggedFilename } from "../../overlay/naming.js";
import type { AppAction } from "../../hooks/useAppReducer.js";
import { useProcessBatch } from "../../hooks/useProcessBatch.js";
import { ErrorBanner } from "./ErrorBanner.js";
import { UploadZone } from "./UploadZone.js";
import { OptionsPanel } from "./OptionsPanel.js";
import { ProgressBar } from "./ProgressBar.js";
import { ResultsGrid } from "./ResultsGrid.js";
import { Lightbox } from "./Lightbox.js";

interface OverlayTabProps {
  state: AppState;
  dispatch: React.Dispatch<AppAction>;
}

export function OverlayTab({ state, dispatch }: OverlayTabProps) {
  const stateRef = useRef(state);
  stateRef.current = state;

  const { startProcessing } = useProcessBatch(stateRef, dispatch);

  const isProcessing = state.phase === "processing";
  const processDisabled =
    isProcessing ||
    state.inputs.length === 0 ||
    state.logo.positions.length === 0 ||
    state.hashtag.positions.length === 0 ||
    state.hashtag.text.trim().length === 0;

  const downloadResultBlob = useCallback((id: string) => {
    const r = stateRef.current.results.find((x) => x.id === id);
    if (!r) return;
    triggerDownload(r.blob, r.filename);
  }, []);

  const handleDownloadZip = useCallback(async () => {
    const selected = stateRef.current.results.filter((r) => r.selected);
    if (selected.length === 0) return;
    try {
      await downloadAsZip(
        selected.map((r) => ({ name: r.filename, blob: r.blob })),
        taggedFilename("batch.zip", "overlay"),
      );
    } catch (err) {
      dispatch({ type: "SET_ERROR", message: `下載失敗: ${(err as Error).message}` });
    }
  }, [dispatch]);

  const lightboxResult = state.lightboxResultId
    ? state.results.find((r) => r.id === state.lightboxResultId) ?? null
    : null;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && stateRef.current.lightboxResultId) {
        dispatch({ type: "CLOSE_LIGHTBOX" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [dispatch]);

  return (
    <>
      <ErrorBanner
        message={state.errorMessage}
        onDismiss={() => dispatch({ type: "SET_ERROR", message: null })}
      />

      <section className="section">
        <UploadZone
          inputs={state.inputs}
          disabled={isProcessing}
          onInputsChange={(next) => dispatch({ type: "SET_INPUTS", inputs: next })}
          onError={(msg) => dispatch({ type: "SET_ERROR", message: msg })}
        />
      </section>

      <section className="section">
        <OptionsPanel
          logo={state.logo}
          hashtag={state.hashtag}
          disabled={isProcessing}
          onLogoChange={(logo) => dispatch({ type: "SET_LOGO", logo })}
          onHashtagChange={(hashtag) => dispatch({ type: "SET_HASHTAG", hashtag })}
        />
      </section>

      <div className="section" style={{ background: "transparent", border: "none", padding: 0 }}>
        <button
          className="primary process"
          disabled={processDisabled}
          onClick={() => void startProcessing()}
        >
          {isProcessing ? "處理中…" : "開始處理"}
        </button>
        <ProgressBar
          phase={state.phase}
          progress={state.progress}
          onCancel={() => state.progress?.abortController.abort()}
        />
      </div>

      <section className="section">
        <ResultsGrid
          results={state.results}
          phase={state.phase}
          onToggleSelect={(id) => dispatch({ type: "TOGGLE_SELECT", id })}
          onSelectAll={(sel) => dispatch({ type: "SELECT_ALL", selected: sel })}
          onDownloadZip={() => void handleDownloadZip()}
          onDownloadOne={downloadResultBlob}
          onOpenLightbox={(id) => dispatch({ type: "OPEN_LIGHTBOX", id })}
        />
      </section>

      <Lightbox
        result={lightboxResult}
        onClose={() => dispatch({ type: "CLOSE_LIGHTBOX" })}
        onDownload={downloadResultBlob}
      />
    </>
  );
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 100);
}


